import type { PostHogContextRef } from '../context'

/**
 * Outcome of a Discover search — drives the `community_discover_search_completed.state`
 * prop. `error` covers the AI-search-stream `'ERROR'` state.
 */
export type CommunityDiscoverSearchState = 'success' | 'error'

/**
 * Community Discover-tab events. Fire from
 * `lib/components/Community/Identity/Library/PlaylistTabs/DiscoverTab/` and
 * the underlying `useDiscoverTabState` hook.
 *
 * Notes:
 * - `community_discover_refined` derives `refinement_length` and
 *   `refinement_word_count` from the raw refinement text rather than capturing
 *   the free text itself — keeps user-typed content out of PostHog (PII +
 *   cardinality). A future Oracle protocol change can add a typed
 *   `refinement_type` argument when `<Gaia type="refine" text="..." />`
 *   exposes structured fields beyond the raw text.
 * - `community_discover_searched` + `community_discover_search_completed` are
 *   the search-effectiveness pair: pair them in a funnel to get zero-result
 *   rate, search-error rate, and search → follow / click conversion.
 */
export type DiscoverEvents = {
  community_discover_viewed: () => void
  /**
   * Fires when a user follows a playlist from a Discover row (FollowTag
   * click). Carries `position` so the discover-funnel analysis (view → follow
   * by rank) keeps working. Coexists with the generic `community_playlist_followed`
   * event upstream — both fire on a discover-row follow, same pattern as
   * `community_playlist_item_played` (generic) + `community_discover_video_clicked`
   * (surface-specific) firing together on tile clicks.
   */
  community_discover_playlist_followed: (playlistId: string, position: number) => void
  community_discover_video_clicked: (
    contentId: string,
    playlistId: string,
    position: number,
    contentTitle?: string,
  ) => void
  community_discover_refined: (refinementValue: string) => void
  community_discover_searched: (isRefinement: boolean, refineTagCount?: number) => void
  community_discover_search_completed: (
    resultCount: number,
    hadResults: boolean,
    state: CommunityDiscoverSearchState,
  ) => void
}

export function createDiscoverEventCallbacks (
  contextRef: PostHogContextRef,
): DiscoverEvents {
  return {
    community_discover_viewed: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_discover_viewed', {
        context: 'community',
      })
    },

    community_discover_playlist_followed: (playlistId: string, position: number) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_discover_playlist_followed', {
        context: 'community',
        playlist_id: playlistId,
        position,
      })
    },

    community_discover_video_clicked: (
      clickedContentId: string,
      playlistId: string,
      position: number,
      contentTitle?: string,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_discover_video_clicked', {
        context: 'community',
        content_id: clickedContentId,
        playlist_id: playlistId,
        position,
        ...(contentTitle && { content_title: contentTitle }),
      })
    },

    community_discover_refined: (refinementValue: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      // Derive analytical properties from the raw refinement text rather than
      // sending the text itself — keeps user-typed content out of PostHog
      // (PII + cardinality risk; see notes above).
      const trimmed = refinementValue.trim()
      const wordCount = trimmed === '' ? 0 : trimmed.split(/\s+/).length
      ctx.posthogClient.capture('community_discover_refined', {
        context: 'community',
        refinement_length: trimmed.length,
        refinement_word_count: wordCount,
      })
    },

    community_discover_searched: (isRefinement: boolean, refineTagCount?: number) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_discover_searched', {
        context: 'community',
        is_refinement: isRefinement,
        ...(refineTagCount != null && { refine_tag_count: refineTagCount }),
      })
    },

    community_discover_search_completed: (
      resultCount: number,
      hadResults: boolean,
      state: CommunityDiscoverSearchState,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_discover_search_completed', {
        context: 'community',
        result_count: resultCount,
        had_results: hadResults,
        state,
      })
    },
  }
}
